import { useEffect, useState } from 'react';

import { convertDateToVenueTimezone } from 'utils/date';
import { serverNow } from 'utils/serverTime';
import { venueCurrentTime } from 'utils/scheduleEvaluator';
import { getActiveSplashPath, getNextSplashChangeTime, getSplashImagePaths } from 'utils/splashBranding';
import useResumeSignal from './useResumeSignal';

export const getSplashTickDelay = (nextSplashChangeTime, venueTimezone, now = serverNow()) => {
  if (!nextSplashChangeTime) return null;
  const currentTime = venueTimezone ? convertDateToVenueTimezone(now, venueTimezone) : now;
  return Math.max(nextSplashChangeTime - currentTime.getTime(), 0);
};

const useSplashSchedule = ({ branding, schedules, venueTimezone }) => {
  const [, setRefreshNonce] = useState(0);

  const currentTime = venueCurrentTime(venueTimezone);
  const activeSplashPath = getActiveSplashPath({ branding, schedules, currentTime });
  const nextSplashChangeTime = getNextSplashChangeTime({ branding, schedules, currentTime });

  useEffect(() => {
    if (!nextSplashChangeTime) return;

    const delay = getSplashTickDelay(nextSplashChangeTime, venueTimezone);

    // Trigger next schedule check
    const timeoutId = setTimeout(() => {
      setRefreshNonce(prev => prev + 1);
    }, delay);

    return () => clearTimeout(timeoutId);
  }, [nextSplashChangeTime, venueTimezone]);

  // Resync after sleep/lock/etc.
  useResumeSignal(() => setRefreshNonce(value => value + 1));

  useEffect(() => {
    // Preload splash images when document is loaded to avoid flickering
    const preload = () => {
      const paths = getSplashImagePaths(branding);
      paths.forEach(path => {
        const img = new Image();
        img.src = path;
      });
    };

    const schedulePreload = () =>
      window.requestIdleCallback ? window.requestIdleCallback(preload) : setTimeout(preload, 0);

    if (document.readyState === 'complete') {
      schedulePreload();
      return;
    }

    window.addEventListener('load', schedulePreload);
    return () => window.removeEventListener('load', schedulePreload);
  }, [branding]);

  return { activeSplashPath, nextSplashChangeTime };
};

export default useSplashSchedule;
